// src/context/AddressContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { authFetch } from '@/services/api';

export type Address = {
  addressId: number;
  fullName: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  addressType: 'HOME' | 'WORK' | 'OTHER';
  isDefault: boolean;
  deliveryInstructions?: string;
  createdDate: string;
  updatedDate: string;
};

type AddressContextType = {
  addresses: Address[];
  defaultAddress: Address | null;
  selectedAddress: Address | null;
  setSelectedAddress: (addr: Address | null) => void;
  loading: boolean;
  refreshAddresses: () => Promise<void>;
  createAddress: (
    address: Omit<Address, 'addressId' | 'createdDate' | 'updatedDate'>
  ) => Promise<Address>;
  updateAddress: (
    id: number,
    address: Omit<Address, 'addressId' | 'createdDate' | 'updatedDate'>
  ) => Promise<Address>;
  deleteAddress: (id: number) => Promise<void>;
  setDefaultAddress: (id: number) => Promise<Address>;
  getAddressById: (id: number) => Promise<Address>;
};

const AddressContext = createContext<AddressContextType | undefined>(undefined);

// ────────────────────── SINGLETON FETCH PROMISE ──────────────────────
let addressFetchPromise: Promise<void> | null = null;

export const AddressProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [defaultAddress, setDefaultAddressState] = useState<Address | null>(
    null
  );
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [loading, setLoading] = useState(false);

  // ────────────────────── REFRESH ADDRESSES ──────────────────────
  const refreshAddresses = async () => {
    // reuse in-flight request
    if (addressFetchPromise) {
      return addressFetchPromise;
    }

    if (!user.isAuthenticated) {
      setAddresses([]);
      setDefaultAddressState(null);
      return;
    }

    setLoading(true);

    addressFetchPromise = (async () => {
      try {
        console.log(
          'AddressContext: Fetching addresses for user:',
          user.customerId
        );
        const response = await authFetch('/api/addresses');

        // If backend says "no addresses", clear state
        if (!response.ok) {
          if (response.status === 404) {
            console.log('AddressContext: 404, no addresses for user yet');
            setAddresses([]);
            setDefaultAddressState(null);
          } else {
            // ❗ Any other error: KEEP existing addresses/defaultAddress
            console.warn(
              'AddressContext: Non-200 from /api/addresses, keeping previous state. Status:',
              response.status
            );
          }
          return;
        }

        const addressList = await response.json();
        console.log('AddressContext: Fetched addresses:', addressList);

        setAddresses(addressList);

        // Prefer backend default, otherwise fallback to first address
        const defaultAddr =
          addressList.find((addr: Address) => addr.isDefault) ||
          addressList[0] ||
          null;

        setDefaultAddressState(defaultAddr);

        // ⭐ CRITICAL SYNC
        setSelectedAddress(prev =>
          prev ? prev : defaultAddr
        );
      } catch (error: any) {
        // ❗ Network or parsing error – keep old state
        console.error(
          'AddressContext: Error fetching addresses, preserving previous state:',
          error
        );
      } finally {
        setLoading(false);
        setTimeout(() => {
          addressFetchPromise = null;
        }, 500);
      }
    })();

    return addressFetchPromise;
  };

  // ────────────────────── CREATE ADDRESS ──────────────────────
  const createAddress = async (
    addressData: Omit<Address, 'addressId' | 'createdDate' | 'updatedDate'>
  ): Promise<Address> => {
    console.log('AddressContext: Creating address:', addressData);
    const response = await authFetch('/api/addresses', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(addressData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to create address: ${response.status}`
      );
    }

    const newAddress = await response.json();
    console.log('AddressContext: Created address:', newAddress);
    await refreshAddresses();
    return newAddress;
  };

  // ────────────────────── UPDATE ADDRESS ──────────────────────
  const updateAddress = async (
    id: number,
    addressData: Omit<Address, 'addressId' | 'createdDate' | 'updatedDate'>
  ): Promise<Address> => {
    console.log('AddressContext: Updating address:', id, addressData);
    const response = await authFetch(`/api/addresses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(addressData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to update address: ${response.status}`
      );
    }

    const updatedAddress = await response.json();
    console.log('AddressContext: Updated address:', updatedAddress);
    await refreshAddresses();
    return updatedAddress;
  };

  // ────────────────────── DELETE ADDRESS ──────────────────────
  const deleteAddress = async (id: number): Promise<void> => {
    console.log('AddressContext: Deleting address:', id);
    const response = await authFetch(`/api/addresses/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to delete address: ${response.status}`
      );
    }

    console.log('AddressContext: Deleted address:', id);
    await refreshAddresses();
  };

  // ────────────────────── SET DEFAULT ADDRESS ──────────────────────
  const setDefaultAddress = async (id: number): Promise<Address> => {
    console.log('AddressContext: Setting default address:', id);
    const response = await authFetch(`/api/addresses/${id}/set-default`, {
      method: 'PATCH',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
        `Failed to set default address: ${response.status}`
      );
    }

    const updatedAddress = await response.json();
    console.log('AddressContext: Set default address:', updatedAddress);

    // Optimistic update: set immediately, then refresh in background
    setDefaultAddressState(updatedAddress);
    setSelectedAddress(updatedAddress);
    await refreshAddresses();

    return updatedAddress;
  };

  // ────────────────────── GET ADDRESS BY ID ──────────────────────
  const getAddressById = async (id: number): Promise<Address> => {
    console.log('AddressContext: Fetching address by ID:', id);
    const response = await authFetch(`/api/addresses/${id}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to fetch address: ${response.status}`
      );
    }

    const address = await response.json();
    console.log('AddressContext: Fetched address:', address);
    return address;
  };

  // ────────────────────── AUTO-REFRESH ON AUTH CHANGE ──────────────────────
  useEffect(() => {
    if (user.isAuthenticated && user.customerId) {
      refreshAddresses();
    } else {
      // only clear on logout / missing customer
      setAddresses([]);
      setDefaultAddressState(null);
      setSelectedAddress(null);
    }
  }, [user.isAuthenticated, user.customerId]);

  return (
    <AddressContext.Provider
      value={{
        addresses,
        defaultAddress,
        selectedAddress,
        setSelectedAddress,
        loading,
        refreshAddresses,
        createAddress,
        updateAddress,
        deleteAddress,
        setDefaultAddress,
        getAddressById,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};

export const useAddress = (): AddressContextType => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within AddressProvider');
  }
  return context;
};